'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  fullName: string;
  email: string;
  company: string;
  phone: string;
  monthlyTickets: string;
  currentPlatform: string;
  primaryUseCase: string;
  integrationNeeds: string[];
  preferredDate: string;
  additionalNotes: string;
}

const DemoRequestForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    monthlyTickets: '',
    currentPlatform: '',
    primaryUseCase: '',
    integrationNeeds: [],
    preferredDate: '',
    additionalNotes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const useCaseOptions = [
    'Customer Service Automation',
    'Technical Support',
    'Order Management',
    'Returns & Refunds',
    'Product Information',
    'Account Management',
    'Billing Inquiries',
    'Multi-channel Support'
  ];

  const integrationOptions = [
    'CRM (Salesforce, HubSpot)',
    'Help Desk (Zendesk, Freshdesk)',
    'E-commerce (Shopify, WooCommerce)',
    'Communication (Slack, Teams)',
    'Analytics (Google Analytics)',
    'Custom API Integration'
  ];

  const handleInputChange = (field: keyof FormData, value: string) => {
    if (!isHydrated) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (value: string) => {
    if (!isHydrated) return;
    setFormData(prev => ({
      ...prev,
      integrationNeeds: prev.integrationNeeds.includes(value)
        ? prev.integrationNeeds.filter(item => item !== value)
        : [...prev.integrationNeeds, value]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isHydrated) return;
    
    setIsSubmitting(true);

    try {
      // Send form data to API endpoint
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formType: 'demo',
          ...formData
        }),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setIsSubmitting(false);
      alert('Failed to submit form. Please try again or contact us directly at jeremiah@redletterx.ai');
    }
  };

  if (!isHydrated) {
    return (
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl p-8 lg:p-12 border border-border shadow-lg">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-3/4 mx-auto" />
              <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
              <div className="space-y-4 mt-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-12 bg-muted rounded" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-32 bg-muted/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-card rounded-2xl p-8 lg:p-12 border border-border shadow-lg">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
              <Icon name="RocketLaunchIcon" size={20} variant="solid" />
              <span className="text-sm font-heading font-semibold">Request Demo</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              See AI Support in Your Environment
            </h2>
            <p className="text-lg text-muted-foreground">
              Schedule a personalized demo tailored to your specific use cases and requirements
            </p>
          </div>

          {submitSuccess && (
            <div className="mb-8 p-6 bg-success/10 border border-success/20 rounded-xl flex items-start space-x-3">
              <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
              <div>
                <h4 className="font-heading font-bold text-foreground mb-1">Demo Request Submitted!</h4>
                <p className="text-sm text-muted-foreground">
                  Our team will contact you within 24 hours to schedule your personalized demo.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Work Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Company Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                  placeholder="Your Company"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            {/* Business Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Monthly Support Tickets *
                </label>
                <select
                  required
                  value={formData.monthlyTickets}
                  onChange={(e) => handleInputChange('monthlyTickets', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                >
                  <option value="">Select range</option>
                  <option value="0-1000">0 - 1,000</option>
                  <option value="1000-5000">1,000 - 5,000</option>
                  <option value="5000-10000">5,000 - 10,000</option>
                  <option value="10000+">10,000+</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-heading font-semibold text-foreground">
                  Current Support Platform
                </label>
                <input
                  type="text"
                  value={formData.currentPlatform}
                  onChange={(e) => handleInputChange('currentPlatform', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                  placeholder="e.g., Zendesk, Freshdesk, Custom"
                />
              </div>
            </div>

            {/* Primary Use Case */}
            <div className="space-y-2">
              <label className="block text-sm font-heading font-semibold text-foreground">
                Primary Use Case *
              </label>
              <select
                required
                value={formData.primaryUseCase}
                onChange={(e) => handleInputChange('primaryUseCase', e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
              >
                <option value="">Select primary use case</option>
                {useCaseOptions.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* Integration Needs */}
            <div className="space-y-3">
              <label className="block text-sm font-heading font-semibold text-foreground">
                Integration Requirements
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                {integrationOptions.map((option, index) => (
                  <label key={index} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.integrationNeeds.includes(option)}
                      onChange={() => handleCheckboxChange(option)}
                      className="w-5 h-5 text-accent border-border rounded focus:ring-2 focus:ring-accent"
                    />
                    <span className="text-sm text-foreground">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Preferred Date */}
            <div className="space-y-2">
              <label className="block text-sm font-heading font-semibold text-foreground">
                Preferred Demo Date
              </label>
              <input
                type="date"
                value={formData.preferredDate}
                onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
              />
            </div>

            {/* Additional Notes */}
            <div className="space-y-2">
              <label className="block text-sm font-heading font-semibold text-foreground">
                Additional Notes or Questions
              </label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground resize-none"
                placeholder="Tell us about your specific requirements or questions..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>Request Demo</span>
                  <Icon name="ArrowRightIcon" size={20} />
                </>
              )}
            </button>

            <p className="text-xs text-center text-muted-foreground">
              By submitting this form, you agree to our Privacy Policy and Terms of Service
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default DemoRequestForm;